// ===========================================
// Student Dashboard Content
// ===========================================
// Shared content for both student and parent "student view" dashboards.
// All data is passed as props so the caller controls the data source.

import { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/useAuth';
import { extractSchoolSlugFromPath } from '../../utils/routing';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  ListItemIcon,
  Menu,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  CalendarToday as CalendarIcon,
  Close as CloseIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  ExitToApp as ExitToAppIcon,
  Folder as FolderIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  characterAgeGroupFromName,
  getAgeGroupFromBirthDate,
  getCharacterColor,
  getCharacterName,
  AgeGroup,
} from '../brand';
import { StudentAvatar, AvatarPickerDialog, LayoutToggle, FunHeader, FunDecorations, FunUserMenu } from '.';
import { useStudentLayout } from '../../contexts/StudentLayoutContext';
import { useStudentView } from '../../contexts/useStudentView';
import { StudentPiecesCard } from '../dashboard/parent';
import { useDownloadResource } from '../../hooks/useResources';
import { useClassLinks } from '../../hooks/useClassLinks';
import LinkCard from '../classLinks/LinkCard';
import { formatFileSize } from '../../api/resources.api';
import type { StudentDashboardProfile, StudentEnrollment } from '../../api/user.api';
import type { Note } from '../../api/notes.api';
import NoteContent from '../notes/NoteContent';
import type { Resource } from '../../api/resources.api';
import type { Term } from '../../api/admin.api';
import { filterableTerms, noteInTerm, noteYear, termYear } from '../../utils/noteTerms';

// ===========================================
// TYPES
// ===========================================

export interface StudentDashboardContentProps {
  profile: StudentDashboardProfile;
  enrollments: StudentEnrollment[];
  enrollmentsLoading: boolean;
  notes: Note[];
  notesLoading: boolean;
  resources: Resource[];
  resourcesLoading: boolean;
  terms: Term[];
  /** Omit to show the avatar read-only (a parent viewing a withdrawn child, #457). */
  onAvatarChange?: (avatar: string | null) => void;
  avatarChangePending?: boolean;
  /** When true (parent mode): hides logout, shows "Student View" subtitle, fun header has no user menu */
  isParentView?: boolean;
}

// ===========================================
// HELPERS
// ===========================================

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const getInstrumentIcon = (instrument: string | null): string | null => {
  if (!instrument) return null;
  const name = instrument.toLowerCase();
  if (name.includes('preschool')) return '/instruments/RAINBOW.svg';
  if (name.includes('piano')) return '/instruments/PIANO.svg';
  if (name.includes('guitar') || name.includes('bass')) return '/instruments/GUITAR.svg';
  if (name.includes('drum')) return '/instruments/DRUM.svg';
  if (name.includes('sing') || name.includes('vocal')) return '/instruments/MICROPHONE.svg';
  return null;
};

function VisibilityChip({ visibility }: { visibility: 'PARENTS' | 'STUDENTS' | 'TEACHERS_ONLY' }) {
  const isForStudent = visibility === 'STUDENTS';
  return (
    <Chip
      icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
      label={isForStudent ? 'For You' : 'Parent'}
      size="small"
      color={isForStudent ? 'success' : 'warning'}
      sx={{ height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
    />
  );
}

function NoteItem({
  note,
  showFullContent = false,
  isFunMode = false,
  characterColor,
}: {
  note: Note;
  showFullContent?: boolean;
  isFunMode?: boolean;
  characterColor?: string;
}) {
  // Render the byline only when there is a name to show — a bare "By " is
  // worse than no byline at all.
  const authorName = note.author
    ? `${note.author.firstName} ${note.author.lastName}`.trim()
    : '';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1,
        ...(isFunMode && characterColor && {
          borderRadius: 3,
          border: `2px solid ${characterColor}40`,
          bgcolor: 'rgba(255,255,255,0.9)',
        }),
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">{note.lesson?.name || 'General Note'}</Typography>
          <VisibilityChip visibility={note.visibility} />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {format(new Date(note.createdAt), 'd MMM yyyy')}
        </Typography>
      </Box>
      <NoteContent content={note.content} showFullContent={showFullContent} />
      {authorName && (
        <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
          By {authorName}
        </Typography>
      )}
    </Paper>
  );
}

function AllNotesModal({
  open,
  onClose,
  notes,
  studentName,
  terms: allTerms,
}: {
  open: boolean;
  onClose: () => void;
  notes: Note[];
  studentName: string;
  terms: Term[];
}) {
  // A note is filed by its session day, never its createdAt instant (utils/noteTerms.ts, #272).
  const terms = useMemo(() => filterableTerms(allTerms), [allTerms]);
  const years = useMemo(() => {
    const noteYears = notes.map(noteYear);
    const termYears = terms.map(termYear);
    return [...new Set([...noteYears, ...termYears])].sort((a, b) => b - a);
  }, [notes, terms]);

  const [selectedYear, setSelectedYear] = useState<number | 'all'>('all');
  const [selectedTermId, setSelectedTermId] = useState<string | 'all'>('all');

  const availableTerms = useMemo(
    () => (selectedYear === 'all' ? terms : terms.filter((t) => termYear(t) === selectedYear)),
    [terms, selectedYear]
  );

  const filteredNotes = useMemo(() => {
    let f = notes;
    if (selectedYear !== 'all') f = f.filter((n) => noteYear(n) === selectedYear);
    if (selectedTermId !== 'all') {
      const term = terms.find((t) => t.id === selectedTermId);
      if (term) f = f.filter((n) => noteInTerm(n, term));
    }
    return f.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [notes, selectedYear, selectedTermId, terms]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {studentName}'s Class Notes
          </Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2} sx={{ mb: 3, mt: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Year</InputLabel>
            <Select value={selectedYear} label="Year" onChange={(e) => { setSelectedYear(e.target.value as number | 'all'); setSelectedTermId('all'); }}>
              <MenuItem value="all">All Years</MenuItem>
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Term</InputLabel>
            <Select value={selectedTermId} label="Term" onChange={(e) => setSelectedTermId(e.target.value)}>
              <MenuItem value="all">All Terms</MenuItem>
              {availableTerms.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>
        {filteredNotes.length === 0 ? (
          <Alert severity="info">No notes found for the selected filters.</Alert>
        ) : (
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredNotes.map((note) => <NoteItem key={note.id} note={note} showFullContent />)}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, pl: 2 }}>
          {filteredNotes.length} note{filteredNotes.length !== 1 ? 's' : ''}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function StudentDashboardContent({
  profile,
  enrollments,
  enrollmentsLoading,
  notes,
  notesLoading,
  resources,
  resourcesLoading,
  terms,
  onAvatarChange,
  avatarChangePending = false,
  isParentView = false,
}: StudentDashboardContentProps) {
  const { layoutMode } = useStudentLayout();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const { exitStudentView } = useStudentView();
  const isFunMode = layoutMode === 'fun';

  const handleLogout = async () => {
    await logout();
    const schoolSlug = extractSchoolSlugFromPath(location.pathname);
    navigate(schoolSlug ? `/${schoolSlug}/login` : '/login');
  };

  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [parentMenuAnchor, setParentMenuAnchor] = useState<null | HTMLElement>(null);

  const downloadResource = useDownloadResource();
  // Web links shared on the student's classes. The server scopes these by
  // enrolment (`resolveContentScope`), so a STUDENT sees only their own
  // classes and a PARENT only their family's — `studentId` narrows to this
  // child in both modes. Visibility still applies: a student sees `ALL` links
  // only, so their list is legitimately shorter than a parent's.
  const {
    data: classLinks,
    isLoading: linksLoading,
    isError: linksError,
  } = useClassLinks({ studentId: profile.id });

  // Character derivation
  const ageGroup: AgeGroup = profile.birthDate ? getAgeGroupFromBirthDate(profile.birthDate) : 'KIDS';
  const avatarCharacterMap: Record<string, AgeGroup> = { alice: 'PRESCHOOL', steve: 'KIDS', liam: 'TEENS', floyd: 'ADULT' };
  const avatarCharacter = profile.avatar?.split('/')[0] || '';
  const avatarAgeGroup = avatarCharacterMap[avatarCharacter] || ageGroup;
  const characterColor = getCharacterColor(avatarAgeGroup);
  const characterName = getCharacterName(avatarAgeGroup);
  const defaultAvatar = `${characterName.toLowerCase()}/${characterName.toLowerCase()}-happy.svg`;
  const avatarSrc = `/avatars/${profile.avatar || defaultAvatar}`;

  // Instrument filter
  const instruments = useMemo(() => {
    const seen = new Map<string, string | null>();
    enrollments.forEach((e) => {
      const name = e.lesson.instrument || 'Other';
      if (!seen.has(name)) seen.set(name, e.lesson.instrumentColor || null);
    });
    return Array.from(seen.entries()).map(([name, color]) => ({ name, color }));
  }, [enrollments]);

  const [selectedInstrumentTab, setSelectedInstrumentTab] = useState(0);

  const filteredEnrollments = useMemo(() => {
    if (selectedInstrumentTab === 0 || instruments.length <= 1) return enrollments;
    const sel = instruments[selectedInstrumentTab - 1]?.name;
    return enrollments.filter((e) => (e.lesson.instrument || 'Other') === sel);
  }, [enrollments, selectedInstrumentTab, instruments]);

  const filteredLessonIds = useMemo(() => {
    if (selectedInstrumentTab === 0 || instruments.length <= 1) return undefined;
    return filteredEnrollments.map((e) => e.lesson.id);
  }, [filteredEnrollments, selectedInstrumentTab, instruments]);

  const enrollmentsByDay = useMemo(() => {
    const grouped: Record<number, typeof filteredEnrollments> = {};
    filteredEnrollments.forEach((e) => {
      const day = e.lesson.dayOfWeek;
      if (!grouped[day]) grouped[day] = [];
      grouped[day].push(e);
    });
    return grouped;
  }, [filteredEnrollments]);

  // Notes — parents see all; students see only STUDENTS-visible
  const visibleNotes = useMemo(() => {
    const filtered = isParentView ? notes : notes.filter((n) => n.visibility === 'STUDENTS');
    return filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [notes, isParentView]);

  const recentNotes = useMemo(() => visibleNotes.slice(0, 5), [visibleNotes]);

  // Card colours
  const cardColors = {
    header: { border: characterColor, bg: `${characterColor}15` },
    schedule: { border: '#4580E4', bg: 'rgba(69, 128, 228, 0.08)' },
    notes: { border: '#FFCE00', bg: 'rgba(255, 206, 0, 0.12)' },
    pieces: { border: '#FFAE9E', bg: 'rgba(255, 174, 158, 0.12)' },
    resources: { border: '#96DAC9', bg: 'rgba(150, 218, 201, 0.15)' },
  };

  const getFunCardSx = (color: { border: string; bg: string }) =>
    isFunMode ? { borderRadius: 4, border: `3px solid ${color.border}`, bgcolor: color.bg, boxShadow: '4px 4px 0px rgba(0,0,0,0.08)' } : {};

  const getFunTitleSx = (color: string) =>
    isFunMode ? { fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive', color } : {};

  // ===========================================
  // DASHBOARD CONTENT (shared between modes)
  // ===========================================

  const dashboardContent = (
    <>
      {/* Student Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          bgcolor: isFunMode ? cardColors.header.bg : `${characterColor}15`,
          borderLeft: isFunMode ? 'none' : `4px solid ${characterColor}`,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          ...(isFunMode && {
            borderRadius: 4,
            border: `3px solid ${cardColors.header.border}`,
            boxShadow: '4px 4px 0px rgba(0,0,0,0.08)',
          }),
        }}
      >
        <Box
          sx={{
            width: isFunMode ? 80 : 64,
            height: isFunMode ? 80 : 64,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isFunMode ? 3 : 1,
            flexShrink: 0,
            cursor: onAvatarChange ? 'pointer' : 'default',
            border: isFunMode ? `3px solid ${cardColors.header.border}` : 'none',
            ...(onAvatarChange && { '&:hover': { boxShadow: isFunMode ? 5 : 3, transform: 'scale(1.05)' } }),
            transition: 'all 0.2s',
          }}
          onClick={onAvatarChange ? () => setAvatarPickerOpen(true) : undefined}
          title={onAvatarChange ? 'Click to change avatar' : undefined}
        >
          <StudentAvatar
            avatar={profile.avatar}
            birthDate={profile.birthDate}
            firstName={profile.firstName}
            lastName={profile.lastName}
            size={isFunMode ? 70 : 56}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant={isFunMode ? 'h4' : 'h5'}
            fontWeight="bold"
            color="text.primary"
            sx={isFunMode ? { fontFamily: '"Monkey Mayhem", "Comic Sans MS", cursive', color: characterColor } : {}}
          >
            {profile.firstName} {profile.lastName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <SchoolIcon fontSize="small" sx={{ color: characterColor }} />
            <Typography variant="body2" color="text.secondary">
              {profile.enrollmentCount > 0
                ? `${profile.enrollmentCount} ${profile.enrollmentCount === 1 ? 'class' : 'classes'} enrolled`
                : 'No active enrollments'}
            </Typography>
            {profile.attendanceRate !== null && (
              <>
                <Typography variant="body2" color="text.disabled">|</Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(profile.attendanceRate)}% attendance
                </Typography>
              </>
            )}
          </Box>

          {/* Instrument filter chips */}
          {instruments.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label="All"
                size="small"
                onClick={() => setSelectedInstrumentTab(0)}
                sx={{
                  fontWeight: 600,
                  bgcolor: selectedInstrumentTab === 0 ? characterColor : 'transparent',
                  color: selectedInstrumentTab === 0 ? 'white' : 'text.secondary',
                  border: selectedInstrumentTab === 0 ? 'none' : '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: selectedInstrumentTab === 0 ? characterColor : `${characterColor}20` },
                }}
              />
              {instruments.map((inst, idx) => {
                const color = inst.color || characterColor;
                const isSelected = selectedInstrumentTab === idx + 1;
                return (
                  <Chip
                    key={inst.name}
                    label={inst.name}
                    size="small"
                    onClick={() => setSelectedInstrumentTab(idx + 1)}
                    sx={{
                      fontWeight: 600,
                      bgcolor: isSelected ? color : 'transparent',
                      color: isSelected ? 'white' : color,
                      border: '1.5px solid',
                      borderColor: color,
                      '&:hover': { bgcolor: isSelected ? color : `${color}20` },
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        {/* Fun mode navigation buttons (only in fun mode) */}
        {isFunMode && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Dashboard button — only relevant in student's own view, not parent view */}
            {!isParentView && (
              <Button
                variant={location.pathname.endsWith('/student') ? 'contained' : 'outlined'}
                size="small"
                startIcon={<DashboardIcon />}
                onClick={() => navigate('.')}
                sx={{
                  borderColor: characterColor,
                  color: location.pathname.endsWith('/student') ? 'white' : characterColor,
                  bgcolor: location.pathname.endsWith('/student') ? characterColor : 'transparent',
                  '&:hover': { borderColor: characterColor, bgcolor: location.pathname.endsWith('/student') ? characterColor : `${characterColor}20` },
                  fontWeight: 600,
                  borderRadius: 2,
                }}
              >
                Dashboard
              </Button>
            )}
            <Button
              variant={location.pathname.includes('/messages') ? 'contained' : 'outlined'}
              size="small"
              startIcon={<EmailIcon />}
              onClick={() => navigate('messages')}
              sx={{
                borderColor: characterColor,
                color: location.pathname.includes('/messages') ? 'white' : characterColor,
                bgcolor: location.pathname.includes('/messages') ? characterColor : 'transparent',
                '&:hover': { borderColor: characterColor, bgcolor: location.pathname.includes('/messages') ? characterColor : `${characterColor}20` },
                fontWeight: 600,
                borderRadius: 2,
              }}
            >
              Messages
            </Button>
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: `${characterColor}40` }} />
            <LayoutToggle showLabel={false} />
          </Box>
        )}
      </Paper>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {/* My Schedule */}
        <Grid item xs={12} md={6}>
          <Card sx={getFunCardSx(cardColors.schedule)}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={getFunTitleSx(cardColors.schedule.border)}>
                <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle', color: isFunMode ? cardColors.schedule.border : 'inherit' }} />
                My Schedule
              </Typography>
              <Divider sx={{ mb: 2, borderColor: isFunMode ? `${cardColors.schedule.border}40` : 'divider' }} />
              {enrollmentsLoading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : enrollments.length === 0 ? (
                <Alert severity="info" sx={isFunMode ? { borderRadius: 2 } : {}}>No classes enrolled.</Alert>
              ) : (
                <List disablePadding>
                  {Object.entries(enrollmentsByDay)
                    .sort(([a], [b]) => Number(a) - Number(b))
                    .map(([day, dayEnrollments]) => (
                      <Box key={day} sx={{ mb: 2 }}>
                        <Typography
                          variant="subtitle2"
                          color={isFunMode ? cardColors.schedule.border : 'text.secondary'}
                          sx={{ mb: 1, fontWeight: isFunMode ? 700 : 500 }}
                        >
                          {DAY_NAMES[Number(day)]}
                        </Typography>
                        {dayEnrollments.map((enrollment) => {
                          const instrumentColor = enrollment.lesson.instrumentColor || '#4580E4';
                          const programLevelColor = enrollment.lesson.programLevelColor || '#4580E4';
                          const teacherColor = enrollment.lesson.teacherColor || '#4580E4';
                          const instrumentIcon = getInstrumentIcon(enrollment.lesson.instrument);
                          return (
                            <Paper
                              key={enrollment.id}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                mb: 1,
                                borderRadius: isFunMode ? 3 : 2,
                                border: isFunMode ? `3px solid ${instrumentColor}` : `1px solid ${instrumentColor}`,
                                borderLeft: `4px solid ${instrumentColor}`,
                                bgcolor: 'rgba(255,255,255,0.95)',
                                ...(isFunMode && { boxShadow: '3px 3px 0px rgba(0,0,0,0.06)' }),
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                {isFunMode && instrumentIcon && (
                                  <Box sx={{ width: 48, height: 48, borderRadius: 2, bgcolor: `${instrumentColor}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                    <Box component="img" src={instrumentIcon} alt={enrollment.lesson.instrument || ''} sx={{ width: 32, height: 32 }} />
                                  </Box>
                                )}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body1" fontWeight="medium">{enrollment.lesson.name}</Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {enrollment.lesson.startTime} - {enrollment.lesson.endTime}
                                    {enrollment.lesson.room && ` | ${enrollment.lesson.room}`}
                                  </Typography>
                                  <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
                                    {enrollment.lesson.teacher && (
                                      <Chip
                                        label={enrollment.lesson.teacher}
                                        size="small"
                                        sx={{ bgcolor: teacherColor, color: 'white', fontWeight: 500, height: 22, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                                      />
                                    )}
                                    {enrollment.lesson.classmates && enrollment.lesson.classmates.map((classmate) => {
                                      // A school's group name, never cast to the enum (2026-09-09).
                                      const cmAgeGroup = characterAgeGroupFromName(classmate.ageGroup);
                                      const cmCharacter = getCharacterName(cmAgeGroup).toLowerCase();
                                      const cmColor = getCharacterColor(cmAgeGroup);
                                      const cmAvatar = classmate.avatar ? `/avatars/${classmate.avatar}` : `/avatars/${cmCharacter}/${cmCharacter}-happy.svg`;
                                      return (
                                        <Box key={classmate.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: cmColor, borderRadius: 3, pl: 0.25, pr: 1, py: 0.25 }}>
                                          <Box component="img" src={cmAvatar} alt={classmate.firstName} sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: 'white', border: `1.5px solid ${cmColor}`, objectFit: 'contain', p: 0.25 }} />
                                          <Typography variant="caption" sx={{ fontWeight: 600, color: 'white', fontSize: '0.7rem' }}>{classmate.firstName}</Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </Box>
                                {enrollment.lesson.programLevel && (
                                  <Chip label={enrollment.lesson.programLevel} size="small" sx={{ bgcolor: programLevelColor, color: 'white', fontWeight: 500, flexShrink: 0 }} />
                                )}
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Notes */}
        <Grid item xs={12} md={6}>
          <Card sx={getFunCardSx(cardColors.notes)}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={getFunTitleSx(cardColors.notes.border)}>
                  <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle', color: isFunMode ? cardColors.notes.border : 'inherit' }} />
                  Recent Class Notes
                </Typography>
                {visibleNotes.length > 0 && (
                  <Button size="small" endIcon={<ArrowForwardIcon />} onClick={() => setNotesModalOpen(true)} sx={isFunMode ? { color: cardColors.notes.border } : {}}>
                    View All ({visibleNotes.length})
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 2, borderColor: isFunMode ? `${cardColors.notes.border}40` : 'divider' }} />
              {notesLoading ? (
                <Skeleton variant="rectangular" height={150} />
              ) : recentNotes.length === 0 ? (
                <Alert severity="info" sx={isFunMode ? { borderRadius: 2 } : {}}>No notes from teachers yet.</Alert>
              ) : (
                <List disablePadding>
                  {recentNotes.map((note) => <NoteItem key={note.id} note={note} isFunMode={isFunMode} characterColor={characterColor} />)}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* My Pieces */}
        <Grid item xs={12} md={6}>
          <StudentPiecesCard
            studentId={profile.id}
            isFunMode={isFunMode}
            cardSx={getFunCardSx(cardColors.pieces)}
            titleSx={getFunTitleSx(cardColors.pieces.border)}
            dividerColor={cardColors.pieces.border}
            avatarSrc={avatarSrc}
            filterLessonIds={filteredLessonIds}
          />
        </Grid>

        {/* Resources */}
        <Grid item xs={12}>
          <Card sx={getFunCardSx(cardColors.resources)}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={getFunTitleSx(cardColors.resources.border)}>
                <FolderIcon sx={{ mr: 1, verticalAlign: 'middle', color: isFunMode ? cardColors.resources.border : 'inherit' }} />
                Class Resources
              </Typography>
              <Divider sx={{ mb: 2, borderColor: isFunMode ? `${cardColors.resources.border}40` : 'divider' }} />

              {/* Web links (2026-09-08). Rendered ABOVE the files: the file list
                  truncates at 10, so links placed after it could be pushed out
                  of view entirely. Deliberately silent while loading or on
                  error — a link section that renders nothing is correct here,
                  but "no links" must never be *stated* on a failed fetch. */}
              {!linksLoading && !linksError && !!classLinks?.length && (
                <Box sx={{ mb: 2 }}>
                  <Grid container spacing={1.5}>
                    {classLinks.slice(0, 6).map((link) => (
                      <Grid item xs={12} sm={6} md={4} key={link.id}>
                        <LinkCard link={link} />
                      </Grid>
                    ))}
                  </Grid>
                  {classLinks.length > 6 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Showing 6 of {classLinks.length} links
                    </Typography>
                  )}
                  {!!resources.length && (
                    <Divider sx={{ mt: 2, borderColor: isFunMode ? `${cardColors.resources.border}40` : 'divider' }} />
                  )}
                </Box>
              )}

              {resourcesLoading ? (
                <Skeleton variant="rectangular" height={150} />
              ) : resources.length === 0 ? (
                // Only claim "nothing here" when the links are settled and empty too.
                !linksLoading && !linksError && !classLinks?.length ? (
                  <Alert severity="info" sx={isFunMode ? { borderRadius: 2 } : {}}>No shared resources yet.</Alert>
                ) : null
              ) : (
                <List disablePadding>
                  {resources.slice(0, 10).map((resource) => (
                    <Paper
                      key={resource.id}
                      variant="outlined"
                      sx={{ p: 1.5, mb: 1, ...(isFunMode && { borderRadius: 3, border: `2px solid ${cardColors.resources.border}40`, bgcolor: 'rgba(255,255,255,0.9)' }) }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                          <FolderIcon sx={{ color: isFunMode ? cardColors.resources.border : 'primary.main' }} />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap>{resource.fileName}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {resource.Lesson?.name || 'General'} | {formatFileSize(resource.fileSize)}
                            </Typography>
                          </Box>
                        </Box>
                        <Button
                          size="small"
                          onClick={() => downloadResource.mutate(resource.id)}
                          disabled={downloadResource.isPending}
                          variant={isFunMode ? 'contained' : 'text'}
                          sx={isFunMode ? { bgcolor: cardColors.resources.border, color: 'white', '&:hover': { bgcolor: cardColors.resources.border, filter: 'brightness(0.9)' } } : {}}
                        >
                          Download
                        </Button>
                      </Box>
                    </Paper>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );

  return (
    <Box
      sx={{
        ...(isFunMode && {
          bgcolor: '#FCF6E6',
          minHeight: '100vh',
          position: 'relative',
          overflow: 'hidden',
          mx: -3,
          mt: -3,
          px: 3,
          pt: 1,
        }),
      }}
    >
      {isFunMode && <FunDecorations characterName={characterName} />}

      {/* Header */}
      {isFunMode ? (
        <FunHeader
          firstName={profile.firstName}
          characterColor={characterColor}
          userMenuButton={
            isParentView ? (
              <>
                <IconButton
                  onClick={(e) => setParentMenuAnchor(e.currentTarget)}
                  sx={{
                    bgcolor: 'rgba(255,255,255,0.9)',
                    border: `2px solid ${characterColor}`,
                    '&:hover': { bgcolor: 'white' },
                  }}
                >
                  <StudentAvatar
                    avatar={profile.avatar}
                    birthDate={profile.birthDate}
                    firstName={profile.firstName}
                    lastName={profile.lastName}
                    size={32}
                  />
                </IconButton>
                <Menu
                  anchorEl={parentMenuAnchor}
                  open={Boolean(parentMenuAnchor)}
                  onClose={() => setParentMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <MenuItem disabled>
                    <Typography variant="body2" color="text.secondary">
                      {profile.firstName} {profile.lastName}
                    </Typography>
                  </MenuItem>
                  <Divider />
                  <MenuItem onClick={() => { setParentMenuAnchor(null); exitStudentView(); }}>
                    <ListItemIcon><ExitToAppIcon fontSize="small" /></ListItemIcon>
                    Exit Student View
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <FunUserMenu
                studentProfile={profile}
                characterColor={characterColor}
                anchorEl={userMenuAnchor}
                onOpen={(e) => setUserMenuAnchor(e.currentTarget)}
                onClose={() => setUserMenuAnchor(null)}
                onLogout={handleLogout}
                onNavigate={navigate}
              />
            )
          }
        />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {isParentView ? `${profile.firstName}'s Dashboard` : 'Student Dashboard'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {isParentView ? 'Student View' : `Welcome back, ${profile.firstName}!`}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {isParentView && (
              <Button variant="outlined" size="small" startIcon={<EmailIcon />} onClick={() => navigate('messages')}>
                Messages
              </Button>
            )}
            <LayoutToggle />
          </Stack>
        </Box>
      )}

      {isFunMode ? (
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pb: 4 }}>
          {dashboardContent}
        </Container>
      ) : (
        dashboardContent
      )}

      <AllNotesModal
        open={notesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        notes={visibleNotes}
        studentName={profile.firstName}
        terms={terms}
      />

      {onAvatarChange && (
        <AvatarPickerDialog
          open={avatarPickerOpen}
          onClose={() => setAvatarPickerOpen(false)}
          currentAvatar={profile.avatar}
          birthDate={profile.birthDate}
          onSelect={(avatar) => {
            onAvatarChange(avatar);
            setAvatarPickerOpen(false);
          }}
          isLoading={avatarChangePending}
        />
      )}
    </Box>
  );
}
